import { FileText, ShieldCheck, TrendingUp, BarChart3 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { DomainShareScope, QrSharePurpose } from '../../../types/api';

export interface ShareScopeConfig {
  scope: DomainShareScope;
  title: string;
  description: string;
  icon: LucideIcon;
  color: string;
  /** Presentation label only; it never changes which files the scope resolves. */
  defaultPurpose: QrSharePurpose;
  /** Shown when the restaurant has nothing to share in this domain. */
  emptyLabel: string;
}

/**
 * The four options offered when creating a package, in the order the modal shows them.
 * Colours and icons intentionally mirror `smart-sorting/lib/categories.ts` so the same
 * domain reads the same way across the app.
 */
export const SHARE_SCOPES: readonly ShareScopeConfig[] = [
  {
    scope: 'all_compliance',
    title: 'Compartir todos los archivos de Cumplimiento',
    description:
      'Documentos de cumplimiento y evidencia clasificada como cumplimiento.',
    icon: ShieldCheck,
    color: 'var(--brand-600)',
    defaultPurpose: 'audit',
    emptyLabel: 'No hay archivos de Cumplimiento',
  },
  {
    scope: 'all_sales',
    title: 'Compartir todos los archivos de Ventas',
    description: 'Evidencias de ventas subidas en Clasificación.',
    icon: TrendingUp,
    color: 'var(--success-600, #22c55e)',
    defaultPurpose: 'general',
    emptyLabel: 'No hay archivos de Ventas',
  },
  {
    scope: 'all_invoices',
    title: 'Compartir todos los archivos de Facturas',
    description:
      'Facturas originales retenidas y evidencia clasificada como facturas.',
    icon: FileText,
    color: 'var(--warning-500, #f59e0b)',
    defaultPurpose: 'general',
    emptyLabel: 'No hay archivos de Facturas',
  },
  {
    scope: 'all_reports',
    title: 'Compartir todos los Reportes',
    description: 'PDFs de reportes generados disponibles.',
    icon: BarChart3,
    color: 'var(--brand-500, #6366f1)',
    defaultPurpose: 'marketing',
    emptyLabel: 'No hay Reportes',
  },
] as const;

export const SHARE_SCOPE_SHORT_LABELS: Record<DomainShareScope, string> = {
  all_compliance: 'Cumplimiento',
  all_sales: 'Ventas',
  all_invoices: 'Facturas',
  all_reports: 'Reportes',
};

export const PURPOSE_LABELS: Record<QrSharePurpose, string> = {
  audit: 'Auditoría',
  marketing: 'Marketing',
  general: 'General',
};

export function getShareScopeConfig(scope: DomainShareScope): ShareScopeConfig {
  const config = SHARE_SCOPES.find((entry) => entry.scope === scope);
  if (!config) {
    throw new Error(`Unknown share scope: ${scope}`);
  }
  return config;
}

/** Autogenerated, editable package name, e.g. "Ventas — 31 jul 2026". */
export function buildDefaultShareName(
  scope: DomainShareScope,
  now: Date = new Date(),
): string {
  const date = now.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return `${SHARE_SCOPE_SHORT_LABELS[scope]} — ${date}`;
}
